import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Checkbox } from 'ui-components/components/checkbox';
import { Text, TextType } from 'ui-components/components/text';
import { Input } from 'ui-components/components/input';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';

import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import {
  useLabelDpi,
  useLabelOutput,
  useBatchSummaryPage,
  useLabelPrintOrder,
  useLabelLogos,
  useRubberStamp1,
  useRubberStamp2,
  useRubberStamp3,
  useExtendShipDate,
  useExtendShipDateAfter,
} from '../../../../../hooks/use-seller-setting';
import { useUITimeFormatSetting } from '../../../../../hooks/use-user-setting';
import { LayoutSelector } from '../../../../../components/layout-selector';
import { labelDpis, batchSummaryOptions, labelOrderBys, labelMessageOptions } from './constants';

import messages from './messages';
import sharedMessages from '../shared-messages';
import sharedStyle from '../style.module.less';
import style from './style.module.less';
import { findMatchingSettingEnum } from '../helpers';
import TimePicker from './time-picker';
import { emptyObject } from 'ui-components/default-props/empty';

const LABEL_MESSAGE_OPTION_REGEX = /\[(.+?)\]/g;

const renderLabelDpiExplanation = (isOpen, toggleLabelDpiExplanation) => {
  return (
    <div className={classnames(style['dpi-explanation'], { [style.open]: isOpen })}>
      <div className={style['dpi-text-title']}>
        <FormattedMessage {...messages.labelDpiExplanationTitle} />
      </div>
      <div className={style['dpi-text']}>
        <p>
          <FormattedMessage {...messages.labelDpiExplanationFirstParagraph} />
        </p>
        <p>
          <FormattedMessage {...messages.labelDpiExplanationSecondParagraph} />
        </p>
        <p>
          <FormattedMessage {...messages.labelDpiExplanationThirdParagraph} />
        </p>
        <Button className={style['acknowledge-button']} onClick={toggleLabelDpiExplanation} theme='unstyled'>
          <FormattedMessage {...messages.labelDpiExplanationAcknowledgement} />
        </Button>
      </div>
    </div>
  );
};

const LabelMessageOptionsDropdown = ({ id, onDropdownChange }) => {
  const intl = useIntl();
  return (
    <DropdownMenu className={style['no-border']} dropdownMenuShouldHaveWidthOfButton fullWidth id={id} variant='form'>
      <DropdownMenuToggler> </DropdownMenuToggler>
      <DropdownMenuItem key='' onClick={() => onDropdownChange(id, '')}>
        {intl.formatMessage(messages.pleaseSelect)}
      </DropdownMenuItem>
      {labelMessageOptions.map((option) => (
        <DropdownMenuItem key={option.value} onClick={() => onDropdownChange(id, option)}>
          {intl.formatMessage(option.messageDescriptor)}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

LabelMessageOptionsDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  onDropdownChange: PropTypes.func.isRequired,
};

const LabelDocumentOptionsModal = ({
  onInit,
  settingsState = emptyObject,
  onDropdownChange,
  onRadioCheckListChange,
  onCheckboxChange,
  onInputChange,
}) => {
  const intl = useIntl();
  const userTimeFormat = useUITimeFormatSetting();
  const labelOutputSetting = useLabelOutput();
  const labelDpiSetting = useLabelDpi();
  const batchSummaryPageSetting = useBatchSummaryPage();
  const labelPrintOrderSetting = useLabelPrintOrder();
  const enableLabelLogos = useLabelLogos();

  const rubberStamp1 = useRubberStamp1();
  const rubberStamp2 = useRubberStamp2();
  const rubberStamp3 = useRubberStamp3();
  const extendShipDateSetting = useExtendShipDate();
  const extendShipDateAfterSetting = useExtendShipDateAfter();
  const initialSettingsState = useMemo(
    () => ({
      [SellerSettings.labelOutput.key]: labelOutputSetting,
      [SellerSettings.labelDpi.key]: labelDpiSetting,
      [SellerSettings.batchSummaryPage.key]: batchSummaryPageSetting,
      [SellerSettings.labelPrintOrder.key]: labelPrintOrderSetting,
      [SellerSettings.enableLabelLogos.key]: enableLabelLogos,
      [SellerSettings.rubberStamp1.key]: rubberStamp1,
      [SellerSettings.rubberStamp2.key]: rubberStamp2,
      [SellerSettings.rubberStamp3.key]: rubberStamp3,
      [SellerSettings.extendShipDate.key]: extendShipDateSetting,
      [SellerSettings.extendShipDateAfter.key]: extendShipDateAfterSetting,
    }),
    [
      batchSummaryPageSetting,
      labelOutputSetting,
      labelDpiSetting,
      labelPrintOrderSetting,
      rubberStamp3,
      rubberStamp2,
      rubberStamp1,
      extendShipDateAfterSetting,
      extendShipDateSetting,
      enableLabelLogos,
    ],
  );

  useEffect(() => {
    onInit(initialSettingsState);
  }, [onInit, initialSettingsState]);

  const [labelDpiTextIsOpen, setLabelDpiTextIsOpen] = useState(false);
  const toggleLabelDpiExplanation = useCallback(() => {
    setLabelDpiTextIsOpen((prevIsOpen) => !prevIsOpen);
  }, [setLabelDpiTextIsOpen]);

  const onLabelOutputChange = useCallback(
    (value) => onRadioCheckListChange(value, SellerSettings.labelOutput.key),
    [onRadioCheckListChange],
  );
  const labelFormat = settingsState[SellerSettings.labelOutput.key] || initialSettingsState[SellerSettings.labelOutput.key];
  const labelDpi = settingsState[SellerSettings.labelDpi.key] || initialSettingsState[SellerSettings.labelDpi.key];
  const batchSummaryPage = settingsState[SellerSettings.batchSummaryPage.key] || initialSettingsState[SellerSettings.batchSummaryPage.key];
  const labelPrintOrder = settingsState[SellerSettings.labelPrintOrder.key] || initialSettingsState[SellerSettings.labelPrintOrder.key];
  const printLogos = settingsState[SellerSettings.enableLabelLogos.key];
  const [isEditingRubberStamps, setIsEditingRubberStamps] = useState({
    [SellerSettings.rubberStamp1.key]: false,
    [SellerSettings.rubberStamp2.key]: false,
    [SellerSettings.rubberStamp3.key]: false,
  });
  const message1 =
    settingsState[SellerSettings.rubberStamp1.key] ||
    (!isEditingRubberStamps[SellerSettings.rubberStamp1.key] ? initialSettingsState[SellerSettings.rubberStamp1.key] : null);
  const message2 =
    settingsState[SellerSettings.rubberStamp2.key] ||
    (!isEditingRubberStamps[SellerSettings.rubberStamp2.key] ? initialSettingsState[SellerSettings.rubberStamp2.key] : null);
  const message3 =
    settingsState[SellerSettings.rubberStamp3.key] ||
    (!isEditingRubberStamps[SellerSettings.rubberStamp3.key] ? initialSettingsState[SellerSettings.rubberStamp3.key] : null);
  const extendShipDate = settingsState[SellerSettings.extendShipDate.key];
  const extendShipDateAfter =
    settingsState[SellerSettings.extendShipDateAfter.key] || initialSettingsState[SellerSettings.extendShipDateAfter.key];

  // Gets the option selected inside of a message
  const getLabelMessageOptionFromMessage = useCallback((message) => message?.match(LABEL_MESSAGE_OPTION_REGEX)?.[0], []);

  // Gets the label value from all options
  const getLabelMessageOption = useCallback(
    (value) => labelMessageOptions.find((labelMessageOption) => labelMessageOption.value === getLabelMessageOptionFromMessage(value)),
    [getLabelMessageOptionFromMessage],
  );

  const [rubberStamp1Option, setRubberStamp1Option] = useState(getLabelMessageOption(message1));
  const [rubberStamp2Option, setRubberStamp2Option] = useState(getLabelMessageOption(message2));
  const [rubberStamp3Option, setRubberStamp3Option] = useState(getLabelMessageOption(message3));

  // Saves the option selected from dropdown
  const handleOnDropdownChange = useCallback(
    (id, option, rubberHandler) => {
      rubberHandler(option);
      onDropdownChange(id, option.value);
    },
    [onDropdownChange],
  );

  const getInputValue = useCallback(
    (value, option) => {
      if (option?.messageDescriptor && value) {
        const optionText = intl.formatMessage(option.messageDescriptor);
        // translated the option to show it using the correct language
        return value.replace(LABEL_MESSAGE_OPTION_REGEX, optionText);
      }
      return value;
    },
    [intl],
  );

  const handleOnLabelMessageInputChange = useCallback(
    (id, rubberStamp) => {
      return (value) => {
        setIsEditingRubberStamps((currentValue) => ({
          ...currentValue,
          [id]: true,
        }));
        // Saves the message with options in english when they are available in message
        onInputChange(id, rubberStamp ? value.replace(LABEL_MESSAGE_OPTION_REGEX, rubberStamp.value) : value);
      };
    },
    [onInputChange],
  );

  return (
    <>
      <BannerMessageV2
        content={<Text textType={TextType.Caption} value={sharedMessages.affectsAllUsersWarning} />}
        layout={BannerLayout.Inline}
        status={BannerStatus.Info}
      />
      <LayoutSelector initalLabelFormat={labelFormat} onSelectLabelFormat={onLabelOutputChange} />
      <div>
        <div className={style.description}>
          <FormattedMessage {...messages.labelDpiLabel} />
        </div>
        <div className={style['options-grid']}>
          <DropdownMenu id={SellerSettings.labelDpi.key} variant='form'>
            <DropdownMenuToggler
              className={sharedStyle['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(labelDpis, labelDpi).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(labelDpis, labelDpi).messageDescriptor)}
            </DropdownMenuToggler>
            {labelDpis.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.labelDpi.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
          <span>
            (
            <Button onClick={toggleLabelDpiExplanation} theme='link'>
              <FormattedMessage {...messages.learnAboutLabelDpi} />
            </Button>
            )
          </span>
        </div>
        {renderLabelDpiExplanation(labelDpiTextIsOpen, toggleLabelDpiExplanation)}
        <div className={style.description}>
          <FormattedMessage {...messages.batchSummaryPageOptionLabel} />
        </div>
        <div className={style['print-option']}>
          <div>
            <FormattedMessage {...messages.batchSummaryPageOptionDescription} />
          </div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.batchSummaryPage.key} variant='form'>
            <DropdownMenuToggler
              className={sharedStyle['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(batchSummaryOptions, batchSummaryPage).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(batchSummaryOptions, batchSummaryPage).messageDescriptor)}
            </DropdownMenuToggler>
            {batchSummaryOptions.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.batchSummaryPage.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <div className={style.description}>
          <FormattedMessage {...messages.orderLabelsBy} />
        </div>
        <div className={style['print-option']}>
          <div>
            <FormattedMessage {...messages.orderLabelsByNote} values={{ a: (chunks) => <b>{chunks}</b> }} />
          </div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.labelPrintOrder.key} variant='form'>
            <DropdownMenuToggler
              className={sharedStyle['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(labelOrderBys, labelPrintOrder).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(labelOrderBys, labelPrintOrder).messageDescriptor)}
            </DropdownMenuToggler>
            {labelOrderBys.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.labelPrintOrder.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <div className={style.description}>
          <FormattedMessage {...messages.labelBranding} />
        </div>
        <div className={style['print-option']}>
          <Checkbox
            checked={!!printLogos}
            label={<FormattedMessage {...messages.printStoreLogos} />}
            name={SellerSettings.enableLabelLogos.key}
            onChange={onCheckboxChange}
          />
        </div>
        <div className={style.description}>
          <FormattedMessage {...messages.labelMessages} />
        </div>
        <div className={style['print-option']}>
          <div>
            <FormattedMessage {...messages.labelMessageDescription} values={{ a: (chunks) => <b>{chunks}</b> }} />
          </div>
          <div className={style['options-grid']}>
            <span className={style['message-label']}>
              <FormattedMessage {...messages.message1} />
            </span>
            <div>
              <Input
                data-testid={SellerSettings.rubberStamp1.key}
                onChange={handleOnLabelMessageInputChange(SellerSettings.rubberStamp1.key, rubberStamp1Option)}
                value={getInputValue(message1, rubberStamp1Option)}
              />
              <LabelMessageOptionsDropdown
                id={SellerSettings.rubberStamp1.key}
                onDropdownChange={(id, option) => handleOnDropdownChange(id, option, setRubberStamp1Option)}
              />
            </div>
          </div>
          <div className={style['options-grid']}>
            <span className={style['message-label']}>
              <FormattedMessage {...messages.message2} />
            </span>
            <div>
              <Input
                data-testid={SellerSettings.rubberStamp2.key}
                onChange={handleOnLabelMessageInputChange(SellerSettings.rubberStamp2.key, rubberStamp2Option)}
                value={getInputValue(message2, rubberStamp2Option)}
              />
              <LabelMessageOptionsDropdown
                id={SellerSettings.rubberStamp2.key}
                onDropdownChange={(id, option) => handleOnDropdownChange(id, option, setRubberStamp2Option)}
              />
            </div>
          </div>
          <div className={style['options-grid']}>
            <span className={style['message-label']}>
              <FormattedMessage {...messages.message3} />
            </span>
            <div>
              <Input
                data-testid={SellerSettings.rubberStamp3.key}
                onChange={handleOnLabelMessageInputChange(SellerSettings.rubberStamp3.key, rubberStamp3Option)}
                value={getInputValue(message3, rubberStamp3Option)}
              />
              <LabelMessageOptionsDropdown
                id={SellerSettings.rubberStamp3.key}
                onDropdownChange={(id, option) => handleOnDropdownChange(id, option, setRubberStamp3Option)}
              />
            </div>
          </div>
        </div>
        <div className={style.description}>
          <FormattedMessage {...messages.shippingCutoff} />
        </div>
        <div className={style['options-grid']}>
          <Checkbox
            checked={!!extendShipDate}
            label={<FormattedMessage {...messages.shippingCutoffMessage} />}
            name={SellerSettings.extendShipDate.key}
            onChange={onCheckboxChange}
          />
          <TimePicker
            id={SellerSettings.extendShipDateAfter.key}
            onTimeSelected={onDropdownChange}
            selectedValue={extendShipDateAfter}
            timeFormat={userTimeFormat}
            togglerClassName={sharedStyle['dropdown-toggler']}
          />
        </div>
      </div>
    </>
  );
};

LabelDocumentOptionsModal.propTypes = {
  onInit: PropTypes.func.isRequired,
  onDropdownChange: PropTypes.func.isRequired,
  onRadioCheckListChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  settingsState: PropTypes.object,
};

export default LabelDocumentOptionsModal;
